import { Table } from "antd";
import React from "react";

const LeadTorch = ({ weldParameters }) => {
  // Filter only LEAD torch parameters and sort by Pass number
  const leadParameters = weldParameters
    .filter((param) => param.Torch === "LEAD")
    .sort(
      (a, b) => parseInt(a.Pass.split(" ")[1]) - parseInt(b.Pass.split(" ")[1])
    );

  // Extract all parameter groups from the first pass (excluding special fields)
  const parameterGroups =
    leadParameters.length > 0
      ? Object.keys(leadParameters[0]).filter(
          (key) =>
            typeof leadParameters[0][key] === "object" &&
            key !== "Type" &&
            key !== "Torch" &&
            ![
              "Pass",
              "PassName",
              "PassPendName",
              "PassGroupNumber",
              "EndingAngle",
              "PassEnable",
            ].includes(key)
        )
      : [];

  // Prepare table data structure
  const tableData = [];

  // First add the pass information rows
  const passInfoFields = [
    { key: "PassName", label: "Pass Name" },
    { key: "PassPendName", label: "Pass Pendant Name" },
    { key: "PassGroupNumber", label: "Pass Group Number" },
    { key: "EndingAngle", label: "Ending Angle" },
    { key: "PassEnable", label: "Pass Enable" },
  ];

  passInfoFields.forEach((field) => {
    const row = {
      key: field.key,
      parameter: field.label,
      values: {},
    };

    leadParameters.forEach((passData) => {
      const passKey = passData.Pass.replace(" ", "");
      row.values[passKey] = [passData[field.key] || ""];
    });

    tableData.push(row);
  });

  // Add an empty row as separator
  tableData.push({
    key: "separator",
    parameter: "",
    values: {},
    isSeparator: true,
  });

  // Then add the Common Weld header and its parameters
  tableData.push({
    key: "CommonWeld",
    parameter: "Common Weld",
    isRedHeader: true,
    values: {},
  });

  // Add Common Weld parameters
  if (leadParameters[0]?.CommonWeld) {
    Object.keys(leadParameters[0].CommonWeld).forEach((param) => {
      const row = {
        key: `CommonWeld-${param}`,
        parameter: param,
        values: {},
      };

      leadParameters.forEach((passData) => {
        const passKey = passData.Pass.replace(" ", "");
        row.values[passKey] = passData.CommonWeld
          ? [passData.CommonWeld[param]]
          : [""];
      });

      tableData.push(row);
    });
  }

  // Add other parameter groups
  parameterGroups.forEach((group) => {
    if (group === "CommonWeld") return; // Already added

    // Add the red header row
    tableData.push({
      key: group,
      parameter: group,
      isRedHeader: true,
      values: {},
    });

    // Get all parameters under this group from the first pass
    const groupParameters = leadParameters[0][group]
      ? Object.keys(leadParameters[0][group])
      : [];

    // Add each parameter under this group
    groupParameters.forEach((param) => {
      const row = {
        key: `${group}-${param}`,
        parameter: param,
        values: {},
      };

      // Map values from each pass
      leadParameters.forEach((passData) => {
        const passKey = passData.Pass.replace(" ", "");
        row.values[passKey] = passData[group] ? [passData[group][param]] : [""];
      });

      tableData.push(row);
    });
  });

  // Prepare column groups based on passes
  const columnGroups = leadParameters.map((pass) => ({
    title: pass.Pass,
    key: pass.Pass.replace(" ", ""),
  }));

  // Helper function for cell rendering
  const renderCell = (value) => {
    if (!value || !value[0]) return "";
    return value[0];
  };

  // Columns configuration
  const columns = [
    {
      title: "",
      dataIndex: "parameter",
      key: "parameter",
      fixed: "left",
      width: 200,
      render: (text, record) => {
        if (record.isSeparator) {
          return null;
        }
        return (
          <span
            style={{
              color: record.isRedHeader ? "#e03540" : "inherit",
              fontWeight: record.isRedHeader ? "bold" : "normal",
            }}
          >
            {text}
          </span>
        );
      },
      onCell: (record) => {
        if (record.isSeparator) {
          return { colSpan: 0 };
        }
        if (record.isRedHeader) {
          return { colSpan: 1 };
        }
        return {};
      },
    },
    ...columnGroups.map((group) => ({
      title: <span style={{ color: "#e03540" }}>{group.title}</span>,
      dataIndex: ["values", group.key],
      key: group.key,
      render: renderCell,
      onCell: (record) => {
        if (record.isSeparator || record.isRedHeader) {
          return { colSpan: 0 };
        }
        return {};
      },
    })),
  ];

  const calcHeight = -190;
  const tableHeight = `calc(${calcHeight}px + 100vh)`;
  return (
    <div
      className="lead-torch-table"
      style={{
        height: tableHeight,
        position: "relative",
      }}
    >
      <Table
        columns={columns}
        dataSource={tableData}
        className="welding-parameters-table"
        pagination={false}
        bordered
        scroll={{
          x: 2800,
          y: tableHeight,
        }}
      />
    </div>
  );
};

export default LeadTorch;
